import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/apiClient'
import { getHttpErrorMessage, getHttpStatus } from '@/lib/httpErrors'
import type { MyBetSummary } from '@/types/miniApp'

/** SGP V2 BLACK -- Personalized SGP Lab (Sprint "SGP V2 BLACK: Personalized SGP Lab",
 * 05/09/2026). Los tipos reflejan EXACTAMENTE lo que devuelve `app/api/sgp_lab.py`/
 * `app/sgp_lab/service.py` -- nunca se fabrica un campo que el backend no envía. Mismo
 * patrón que `useParlayLab.ts`, adaptado a sesiones/intentos por fixture (Sección 5 del
 * encargo, en vez de un cupo diario plano). */

export type SGPLabFamily = 'resultado' | 'goles' | 'corners' | 'tarjetas' | 'tiros' | 'jugadores'
export type SGPLabStatus = 'valid' | 'no_sgp' | 'target_not_achievable' | 'informacion_insuficiente'

export interface SGPLabSessionInfo {
  session_id: number
  fixture_id: number
  attempts_used: number
  attempts_limit: number | null
  attempts_remaining: number | null
  unlimited: boolean
}

export interface SGPLabAllowance {
  sessions_used: number
  sessions_limit: number | null
  sessions_remaining: number | null
  attempts_per_session_limit: number | null
  unlimited: boolean
  resets_at: string
}

export interface SGPLabFixtureSummary {
  fixture_id: number
  competition_name: string | null
  competition_country: string | null
  home_team: string
  away_team: string
  home_logo: string | null
  away_logo: string | null
  kickoff_utc: string
  coverage_status: 'SUPPORTED' | 'LIMITED'
}

export interface SGPLabFamilyAvailability {
  family: SGPLabFamily
  label: string
  available: boolean
}

/** Odds Truth real (Sección 9 del encargo, 24/09/2026) -- MISMA forma que JA Lab/Parlay Lab
 * (`JALabOddsSource`/`ParlayLabOddsSource`), nunca un bookmaker inventado. */
export interface SGPLabOddsSource {
  bookmaker: string | null
  source: string | null
  fetched_at: string | null
  reference_text: string
}

export interface SGPLabLeg {
  market: string
  market_label: string
  family: SGPLabFamily | null
  family_label: string | null
  selection: string
  selection_label: string
  // JA reference odds (cuota de MERCADO) -- INMUTABLE desde la generación.
  odds: number | null
  // Cuota JUSTA de John Anthony (1/model_probability) -- Sección 9 del encargo.
  fair_odds?: number | null
  model_probability: number | null
  confidence_score: number | null
  risk_score: number | null
  data_quality_score: number | null
  // Odds Truth real -- bookmaker observado + cuándo se capturó (Sección 9 del encargo).
  odds_source?: SGPLabOddsSource
}

export interface SGPLabExplanation {
  why: string[]
  risks: string[]
  watch: string[]
}

// Corrección real de usuario (05/09/2026, Sección 2 -- historial): SGP Lab es de UN
// fixture, y sus legs (a diferencia de Parlay Lab) nunca cargan nombres de equipo -- se
// resuelve en el backend en lectura (`app/sgp_lab/service.py::_fixture_summaries`).
export interface SGPLabFixtureInfo {
  home_team: string | null
  away_team: string | null
  competition_name: string | null
  kickoff_utc: string | null
}

export interface SGPLabResult {
  id: number
  session_id: number
  fixture_id: number
  fixture: SGPLabFixtureInfo | null
  status: SGPLabStatus
  requested_families: SGPLabFamily[]
  target_odds_min: number | null
  target_odds_max: number | null
  legs: SGPLabLeg[]
  // Sección 3/8 del encargo: "estas legs van juntas porque describen el mismo escenario
  // probable del partido" -- narrativa real, nunca inventada por el frontend.
  thesis: string | null
  correlation_relationships: string[]
  combined_odds: number | null
  joint_probability_raw: number | null
  joint_probability_adjusted: number | null
  quality_score: number | null
  risk_score: number | null
  correlation_level: string | null
  correlation_reasoning: string | null
  explanation: SGPLabExplanation
  failure_reason: string | null
  target_not_achievable_reason: string | null
  candidates_total: number
  combinations_evaluated: number
  generation_ms: number | null
  created_at: string
  session: SGPLabSessionInfo | null
  // Sprint "Mi Banca V2" (10/09/2026, Sección 1/5 del encargo) -- mismo patrón que
  // `ParlayLabResult`, ver ese docstring.
  linked_personal_bet_id?: number | null
  my_bet?: MyBetSummary | null
  // Sección 5 del encargo (24/09/2026) -- AVAILABLE/PENDING/NOT_AVAILABLE al momento de esta
  // generación, MISMO `compute_lineup_status` que JA Lab. Snapshot, nunca recalculado.
  lineup_status?: 'available' | 'pending' | 'not_available' | null
}

export function getSGPLabErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { data?: { detail?: { message?: string } } } }).response
    const message = response?.data?.detail?.message
    if (message) return message
  }
  // Corrección real de usuario (05/09/2026, ver `useParlayLab.ts::getParlayLabErrorMessage`
  // para el detalle completo del hallazgo): nunca tapar un timeout real con el copy
  // genérico de "no cargó" -- la generación persiste server-side aunque el cliente corte.
  if (error && typeof error === 'object' && (error as { code?: string }).code === 'ECONNABORTED') {
    return 'John Anthony está tardando más de lo esperado construyendo tu SGP. Espera un momento e inténtalo de nuevo -- si vuelve a pasar, revisa tu historial: la generación puede haberse completado igual.'
  }
  return getHttpErrorMessage(getHttpStatus(error))
}

export function getSGPLabErrorCode(error: unknown): string | undefined {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { data?: { detail?: { code?: string } } } }).response
    return response?.data?.detail?.code
  }
  return undefined
}

export function useSGPLabAllowance(enabled: boolean) {
  return useQuery<SGPLabAllowance>({
    queryKey: ['sgp-lab', 'allowance'],
    queryFn: async () => (await apiClient.get('/api/mini-app/sgp-lab/allowance')).data,
    enabled,
    staleTime: 15_000,
  })
}

export function useSGPLabFixtureSearch(enabled: boolean, query: string) {
  return useQuery<{ fixtures: SGPLabFixtureSummary[] }>({
    queryKey: ['sgp-lab', 'fixtures', query],
    queryFn: async () => (await apiClient.get('/api/mini-app/sgp-lab/fixtures', { params: query ? { q: query } : {} })).data,
    enabled,
    staleTime: 60_000,
  })
}

export function useSGPLabFamilies(enabled: boolean, fixtureId: number | null) {
  return useQuery<{ families: SGPLabFamilyAvailability[] }>({
    queryKey: ['sgp-lab', 'families', fixtureId],
    queryFn: async () => (await apiClient.get('/api/mini-app/sgp-lab/families', { params: { fixture_id: fixtureId } })).data,
    enabled: enabled && fixtureId != null,
    staleTime: 60_000,
  })
}

export function useSGPLabHistory(enabled: boolean, offset = 0) {
  return useQuery<{ items: SGPLabResult[] }>({
    queryKey: ['sgp-lab', 'history', offset],
    queryFn: async () => (await apiClient.get('/api/mini-app/sgp-lab/history', { params: { offset } })).data,
    enabled,
    staleTime: 15_000,
  })
}

export function useSGPLabRun(enabled: boolean, runId: number | null) {
  return useQuery<SGPLabResult>({
    queryKey: ['sgp-lab', 'run', runId],
    queryFn: async () => (await apiClient.get(`/api/mini-app/sgp-lab/runs/${runId}`)).data,
    enabled: enabled && runId != null,
  })
}

export interface GenerateSGPLabInput {
  fixtureId: number
  families: SGPLabFamily[]
  targetOddsMin?: number | null
  targetOddsMax?: number | null
}

// Corrección real de usuario (05/09/2026, ver `useParlayLab.ts::GENERATE_TIMEOUT_MS`): misma
// arquitectura de generación en frío (refresco de odds/lineup + búsqueda combinatoria) que
// Parlay Lab -- mismo riesgo real de exceder el timeout global de 30s del cliente. nginx
// ahora tolera 200s; se usa el mismo margen real de 190s solo para este endpoint lento.
const GENERATE_TIMEOUT_MS = 190_000

export function useGenerateSGPLab() {
  const queryClient = useQueryClient()
  return useMutation<SGPLabResult, unknown, GenerateSGPLabInput>({
    mutationFn: async ({ fixtureId, families, targetOddsMin, targetOddsMax }) =>
      (await apiClient.post(
        '/api/mini-app/sgp-lab/generate',
        {
          fixture_id: fixtureId, families,
          target_odds_min: targetOddsMin ?? null, target_odds_max: targetOddsMax ?? null,
        },
        { timeout: GENERATE_TIMEOUT_MS }
      )).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sgp-lab', 'allowance'] })
      queryClient.invalidateQueries({ queryKey: ['sgp-lab', 'history'] })
    },
  })
}

/** Modo B -- "Enséñame opciones" (Sección 11 del encargo, 24/09/2026). Cada opción ya es un
 * `SGPLabResult` real e independiente (su propio `id`, persistido) -- "usar esta opción" no
 * necesita ningún endpoint nuevo, ya es un run consultable/jugable con la infraestructura
 * existente (`useSGPLabRun`, `RegisterBetModal`). */
export function useGenerateSGPLabOptions() {
  const queryClient = useQueryClient()
  return useMutation<{ options: SGPLabResult[] }, unknown, GenerateSGPLabInput>({
    mutationFn: async ({ fixtureId, families, targetOddsMin, targetOddsMax }) =>
      (await apiClient.post(
        '/api/mini-app/sgp-lab/options',
        {
          fixture_id: fixtureId, families,
          target_odds_min: targetOddsMin ?? null, target_odds_max: targetOddsMax ?? null,
        },
        { timeout: GENERATE_TIMEOUT_MS }
      )).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sgp-lab', 'allowance'] })
      queryClient.invalidateQueries({ queryKey: ['sgp-lab', 'history'] })
    },
  })
}
